import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";

import { addBookmark, deleteBookmark } from "api";
import useGetBookmarks from "api/queries/user/useGetBookmarks";
import { ReactComponent as BookmarkIcon } from "assets/images/iconsV2/bookmark.svg";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import "./Bookmark.scss";

interface Props {
  shipmentId: string;
}

function BookmarkTab({ shipmentId }: Props) {
  const intl = useIntl();
  const [isBookmarked, setIsBookmarked] = useState(false);

  const { data: bookmarks, refetch } = useGetBookmarks({
    onError: (err) =>
      errorToast(
        err,
        intl.formatMessage({
          id: "dashboard.bookmarksWidget.couldNotRetrieveBookmarks",
          defaultMessage: "Could not retrieve bookmarks. Please try again later.",
        }),
      ),
  });

  const bookmarkedQuoteRequests = bookmarks?.[0]?.data?.map((bookmark) => ({
    ...bookmark,
    type: "quote_request",
  }));
  const bookmarkedShipments = bookmarks?.[1]?.data?.map((bookmark) => ({
    ...bookmark,
    type: "shipment",
  }));
  const allBookmarks = [...(bookmarkedQuoteRequests ?? []), ...(bookmarkedShipments ?? [])];

  useEffect(() => {
    const bookmarkFound = allBookmarks.some((bookmark) => bookmark.shipment_reference_number === shipmentId);
    if (bookmarkFound) {
      setIsBookmarked(true);
    }
  }, [bookmarks]);

  const { mutate: addBookmarkMutation, isLoading: isAddingBookmark } = useMutation({
    mutationFn: () => addBookmark(shipmentId),
    onSuccess: () => {
      setIsBookmarked(true);
      refetch();
    },
    onError: (err) => errorToast(err),
  });

  const { mutate: deleteBookmarkMutation, isLoading: isDeletingBookmark } = useMutation({
    mutationFn: () => deleteBookmark(shipmentId),
    onSuccess: () => {
      setIsBookmarked(false);
      refetch();
    },
    onError: (err) => errorToast(err),
  });

  const isLoading = isAddingBookmark || isDeletingBookmark;

  const onClickBookmark = () => {
    if (isLoading) return;
    if (isBookmarked) deleteBookmarkMutation();
    else addBookmarkMutation();
  };

  const bookmarkClassName = isBookmarked ? "unbookmark-icon" : "";
  const cursorClassName = isLoading ? "cursor-not-allowed" : "cursor-pointer";

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 0 }}
      overlay={
        <Tooltip id="bookmark-tooltip">
          <span className="text-capitalize">
            <FormattedMessage
              id={isBookmarked ? "general.removeBookmark" : "general.bookmark"}
              defaultMessage={isBookmarked ? "Remove bookmark" : "Bookmark"}
            />
          </span>
        </Tooltip>
      }
    >
      <div
        className={`bookmark-icon ${bookmarkClassName} ${cursorClassName}`}
        style={{ transform: "translateY(1px)" }}
        data-testid={testIds.TOPNAV_V2_BOOKMARK_TOGGLE}
      >
        <BookmarkIcon onClick={onClickBookmark} data-testid={testIds.TOPNAV_V2_BOOKMARK_TOGGLE_ICON} />
      </div>
    </OverlayTrigger>
  );
}

export default BookmarkTab;
